/**
 * Redaction rules for outbound analytics payloads.
 *
 * Kept separate from `posthog.ts` because that module calls `posthog.init()` at
 * import time — this one is pure and therefore testable.
 *
 * The threat these rules address: organisation access codes and referral codes
 * are **bearer credentials**. Whoever holds one can redeem a seat or claim
 * referral credit, and the legacy redemption path does not verify ownership.
 * They must not reach analytics storage, where they are readable by anyone with
 * dashboard access — and where, as `$set`/`$set_once` person properties, they
 * outlive the event that carried them.
 */

/** Query parameters whose value is a credential. `t` is the email-token param. */
export const SENSITIVE_QUERY_PARAMS = ["code", "ref", "token", "t"];

/**
 * Route segments whose FOLLOWING segment is a credential (`/refer/DAK-ABCD`).
 * Query-only redaction misses these entirely.
 *
 * Matched as a path segment anywhere in the path, not as a leading prefix. The app is
 * not always served from the domain root: the `build:*:preview` scripts set
 * `--base=/plumbing/` and friends, and `main.tsx` feeds that basename to the router. A
 * `startsWith("/refer/")` test therefore let `/plumbing/refer/DAK-ABCD` through
 * completely unredacted on every preview deployment.
 */
const SENSITIVE_PATH_RULES = [
  { prefix: ["refer"], credentialOffset: 1 },
  { prefix: ["access-codes"], credentialOffset: 1 },
  { prefix: ["referral", "preview"], credentialOffset: 2 },
  { prefix: ["distribute"], credentialOffset: 1 },
  { prefix: ["codes"], credentialOffset: 1 },
] as const;

/**
 * Property names whose value is a credential, wherever they appear.
 *
 * URL redaction alone is insufficient: every explicitly-named property is
 * unguarded by construction, so one `captureEvent({ access_code })` anywhere
 * defeats it. This closes that whole class centrally rather than depending on
 * every future call site being careful.
 */
export const SENSITIVE_PROPERTY_NAMES = new Set([
  "access_code",
  "referral_code",
  "pending_access_code",
  "pending_referral_code",
]);

export const REDACTED = "REDACTED";

const SENSITIVE_PATH_TEXT_PATTERNS = [
  /((?:^|\/)refer\/)[^/?#\s]+/gi,
  /((?:^|\/)access-codes\/)[^/?#\s]+/gi,
  /((?:^|\/)referral\/preview\/)[^/?#\s]+/gi,
  /((?:^|\/)distribute\/)[^/?#\s]+/gi,
  /((?:^|\/)codes\/)[^/?#\s]+/gi,
] as const;

export function redactSensitiveText(value: unknown): unknown {
  if (typeof value !== "string" || !value) return value;

  let redacted = value.replace(
    /([?&](?:code|ref|token|t)=)[^&#\s]*/gi,
    `$1${REDACTED}`,
  );
  for (const pattern of SENSITIVE_PATH_TEXT_PATTERNS) {
    redacted = redacted.replace(pattern, `$1${REDACTED}`);
  }
  return redacted;
}

/**
 * URL-derived properties PostHog populates automatically. `$initial_current_url`
 * and the session-entry URL are persisted set-once person properties.
 */
export const URL_PROPERTIES = [
  "$current_url",
  "$initial_current_url",
  "$referrer",
  "$initial_referrer",
  "$session_entry_url",
];

/** Redact sensitive query params and path segments. Non-URLs pass through. */
export function redactUrl(value: unknown): unknown {
  if (typeof value !== "string" || !value) return value;
  try {
    // `window` is dereferenced inside the try so a non-browser context fails
    // open — returning the value unchanged rather than throwing and taking all
    // analytics down with it.
    const base =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const url = new URL(value, base);
    let changed = false;

    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.set(param, REDACTED);
        changed = true;
      }
    }

    const redactedPath = redactSensitiveSegments(url.pathname);
    if (redactedPath !== null) {
      url.pathname = redactedPath;
      changed = true;
    }

    return changed ? url.toString() : value;
  } catch {
    return value;
  }
}

/** Redact a bare pathname value, e.g. the manual `page_view.path` property. */
export function redactPath(value: unknown): unknown {
  if (typeof value !== "string" || !value) return value;
  return redactSensitiveSegments(value) ?? value;
}

/**
 * Redacts the segment after any sensitive segment. Returns `null` when nothing matched,
 * so callers can leave the original value untouched rather than round-tripping it.
 */
function redactSensitiveSegments(pathname: string): string | null {
  const parts = pathname.split("/");
  let changed = false;

  for (let start = 0; start < parts.length; start++) {
    for (const rule of SENSITIVE_PATH_RULES) {
      const matches = rule.prefix.every(
        (segment, offset) => parts[start + offset] === segment,
      );
      if (!matches) continue;

      const credentialIndex = start + rule.credentialOffset;
      if (parts[credentialIndex]) {
        parts[credentialIndex] = REDACTED;
        changed = true;
      }
    }
  }

  return changed ? parts.join("/") : null;
}

/**
 * Redact one property bag in place. Applied to `properties`, `$set` and
 * `$set_once` — all three reach PostHog, and the latter two are durable.
 */
export function redactPropertyBag(
  bag: Record<string, any> | undefined | null,
  depth = 0,
): void {
  if (!bag || depth > 2) return;

  for (const key of URL_PROPERTIES) {
    if (key in bag) bag[key] = redactUrl(bag[key]);
  }

  if ("path" in bag) bag.path = redactPath(bag.path);
  if ("$pathname" in bag) bag.$pathname = redactPath(bag.$pathname);

  for (const key of Object.keys(bag)) {
    if (SENSITIVE_PROPERTY_NAMES.has(key) && bag[key] != null) {
      bag[key] = REDACTED;
    } else if (typeof bag[key] === "string") {
      bag[key] = redactSensitiveText(bag[key]);
    }
  }

  // Person properties reach PostHog two ways: as the top-level `$set` /
  // `$set_once` fields of the capture result, and NESTED inside `properties`
  // — which is what `posthog.capture("$set", { $set: props })` and
  // `captureEvent(name, { $set: props })` produce. PostHog honours both, so
  // walking only the top-level bags leaves the nested copy unredacted and
  // makes this hook look complete while credentials still escape.
  for (const nestedKey of ["$set", "$set_once"]) {
    const nested = bag[nestedKey];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      redactPropertyBag(nested as Record<string, any>, depth + 1);
    }
  }
}
